import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { osFetch } from "./api";
import { useOS } from "./OSContext";
import { AIPanel, ConnectedPanel, ChangedPanel, Card, EntityAttentionPanel } from "./shared/panels";
import { FolderKanban, Scale, BookOpen, Map, Flag, FileText, Plus, Loader2, X, ArrowLeft, Archive, Save, Activity, Gauge } from "lucide-react";
import { toast } from "sonner";

// One config per domain — the whole module (list + detail) is data-driven.
export const MODULES = {
  projects: {
    label: "Projects", base: "/projects", entityType: "project", icon: FolderKanban, titleKey: "name", showHealth: true,
    statusOptions: ["idea", "active", "paused", "shipped", "archived"],
    fields: [
      { key: "description", label: "What is this?", type: "textarea" },
      { key: "why", label: "Why does it exist?", type: "textarea" },
      { key: "status", label: "Status", type: "select", options: ["idea", "active", "paused", "shipped", "archived"] },
      { key: "confidence_score", label: "Confidence (0-100)", type: "number" },
      { key: "health_score", label: "Health (0-100)", type: "number" },
    ],
  },
  decisions: {
    label: "Decision Log", base: "/decisions", entityType: "decision", icon: Scale, titleKey: "title",
    statusOptions: ["proposed", "accepted", "superseded", "rejected"],
    fields: [
      { key: "context", label: "Context", type: "textarea" },
      { key: "decision", label: "Decision", type: "textarea" },
      { key: "consequences", label: "Consequences", type: "textarea" },
      { key: "status", label: "Status", type: "select", options: ["proposed", "accepted", "superseded", "rejected"] },
    ],
  },
  bible: {
    label: "Project Bible", base: "/bible", entityType: "bible", icon: BookOpen, titleKey: "title", special: "bible",
    statusOptions: ["draft", "frozen"],
    fields: [{ key: "status", label: "Status", type: "select", options: ["draft", "frozen"] }],
  },
  roadmap: {
    label: "Roadmap", base: "/roadmap", entityType: "roadmap_item", icon: Map, titleKey: "title",
    statusOptions: ["planned", "in_progress", "done", "at_risk"],
    fields: [
      { key: "description", label: "Description", type: "textarea" },
      { key: "horizon", label: "Horizon", type: "select", options: ["now", "next", "later"] },
      { key: "status", label: "Status", type: "select", options: ["planned", "in_progress", "done", "at_risk"] },
    ],
  },
  milestones: {
    label: "Milestones", base: "/milestones", entityType: "milestone", icon: Flag, titleKey: "title",
    statusOptions: ["upcoming", "in_progress", "complete", "at_risk"],
    fields: [
      { key: "description", label: "Description", type: "textarea" },
      { key: "status", label: "Status", type: "select", options: ["upcoming", "in_progress", "complete", "at_risk"] },
      { key: "due_date", label: "Due date", type: "date" },
    ],
  },
  "business-brief": {
    label: "Business Brief", base: "/business-brief", entityType: "business_brief", icon: FileText, titleKey: "title",
    fields: [
      { key: "problem_statement", label: "Problem", type: "textarea" },
      { key: "target_customer", label: "Target customer", type: "textarea" },
      { key: "value_proposition", label: "Value proposition", type: "textarea" },
      { key: "revenue_model", label: "Revenue model", type: "text" },
    ],
    arrays: [{ key: "mvp_features", label: "MVP features" }, { key: "risks", label: "Risks" }],
  },
};

const STATUS_COLOR = {
  active: "text-emerald-300", accepted: "text-emerald-300", done: "text-emerald-300", complete: "text-emerald-300", shipped: "text-emerald-300",
  at_risk: "text-rose-300", rejected: "text-rose-300", superseded: "text-slate-500", paused: "text-amber-300",
  in_progress: "text-cyan-300", proposed: "text-amber-300", planned: "text-slate-300", upcoming: "text-slate-300", idea: "text-slate-300", draft: "text-slate-400", frozen: "text-cyan-300",
};

function Field({ f, value, onChange }) {
  const cls = "os-input";
  if (f.type === "textarea") return <textarea rows={3} className={cls} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`os-field-${f.key}`} />;
  if (f.type === "select") return <select className={cls} value={value || f.options[0]} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`os-field-${f.key}`}>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (f.type === "number") return <input type="number" className={cls} value={value ?? ""} onChange={(e) => onChange(f.key, Number(e.target.value))} data-testid={`os-field-${f.key}`} />;
  if (f.type === "date") return <input type="date" className={cls} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`os-field-${f.key}`} />;
  return <input className={cls} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`os-field-${f.key}`} />;
}

export function EntityList({ moduleKey }) {
  const cfg = MODULES[moduleKey];
  const { can } = useOS();
  const [rows, setRows] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = useCallback(() => osFetch(cfg.base).then(setRows).catch((e) => { setRows([]); toast.error(e.message); }), [cfg.base]);
  useEffect(() => { load(); }, [load]);
  const Icon = cfg.icon;

  return (
    <div className="max-w-5xl" data-testid={`os-${moduleKey}-list`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-2"><Icon size={13} /> {cfg.label}</p>
          <h1 className="font-display font-extrabold text-3xl">{cfg.label}</h1>
        </div>
        {can("create") && <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-3 rounded-2xl font-semibold hover:-translate-y-0.5 transition-transform duration-300" data-testid={`os-${moduleKey}-new-btn`}><Plus size={16} /> New</button>}
      </div>
      {rows === null ? <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div>
        : rows.length === 0 ? <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-slate-500">Nothing here yet.</div> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {rows.map((r) => (
            <Link key={r.id} to={`/os/${moduleKey}/${r.id}`} data-testid={`os-${moduleKey}-item-${r.id}`} className="group rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition-all duration-300">
              <div className="flex items-center gap-2">
                <Icon size={16} className="text-[#00F0FF]" />
                {r.status && <span className={`ml-auto text-[9px] font-mono uppercase tracking-widest ${STATUS_COLOR[r.status] || "text-slate-500"}`}>{r.status}{r.horizon ? ` · ${r.horizon}` : ""}</span>}
              </div>
              <p className="font-display font-bold text-lg mt-3">{r[cfg.titleKey]}</p>
              {cfg.showHealth && <div className="flex gap-3 mt-2 text-[11px] font-mono text-slate-500"><span>health {r.health_score ?? "—"}</span><span>conf {r.confidence_score ?? "—"}</span></div>}
              <p className="text-slate-500 text-sm mt-1 line-clamp-2">{r.description || r.problem_statement || r.decision || r.context || "—"}</p>
            </Link>
          ))}
        </div>
      )}
      {showCreate && <CreateModal cfg={cfg} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateModal({ cfg, onClose, onCreated }) {
  const [form, setForm] = useState({ [cfg.titleKey]: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    if (!form[cfg.titleKey]?.trim()) return;
    setBusy(true);
    try { const r = await osFetch(cfg.base, { method: "POST", body: form }); toast.success("Created"); onCreated(r); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid={`os-${cfg.entityType}-create-modal`}>
        <div className="flex items-center justify-between mb-4"><h2 className="font-display font-bold text-xl">New {cfg.label}</h2><button onClick={onClose}><X size={18} className="text-slate-500" /></button></div>
        <div className="space-y-3">
          <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Title</span><input className="os-input mt-1" autoFocus value={form[cfg.titleKey] || ""} onChange={(e) => set(cfg.titleKey, e.target.value)} data-testid={`os-${cfg.entityType}-title-input`} /></label>
          {cfg.fields.filter((f) => f.type !== "number").map((f) => (
            <label key={f.key} className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{f.label}</span><div className="mt-1"><Field f={f} value={form[f.key]} onChange={set} /></div></label>
          ))}
        </div>
        <button onClick={submit} disabled={busy || !form[cfg.titleKey]?.trim()} className="mt-5 w-full bg-[#0055FF] text-white py-3 rounded-xl font-semibold disabled:opacity-40" data-testid={`os-${cfg.entityType}-create-submit`}>{busy ? "Creating…" : "Create"}</button>
      </div>
    </div>
  );
}

export function EntityDetail({ moduleKey }) {
  const cfg = MODULES[moduleKey];
  const { id } = useParams();
  const { can } = useOS();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => osFetch(`${cfg.base}/${id}`).then((d) => { setData(d); setEdit(null); }).catch((e) => toast.error(e.message)), [cfg.base, id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div>;
  const e = data.entity, live = data.live;
  const Icon = cfg.icon;
  const ed = edit || {};

  const startEdit = () => {
    const base = { [cfg.titleKey]: e[cfg.titleKey] };
    cfg.fields.forEach((f) => { base[f.key] = e[f.key]; });
    if (cfg.special === "bible") base.sections = (e.sections || []).map((s) => ({ ...s }));
    setEdit(base);
  };
  const save = async () => {
    setSaving(true);
    try { await osFetch(`${cfg.base}/${id}`, { method: "PATCH", body: { ...edit, expected_version: e.current_version } }); toast.success("Saved"); load(); }
    catch (err) { toast.error(err.status === 409 ? "Someone else edited this — reloading." : err.message); if (err.status === 409) load(); }
    finally { setSaving(false); }
  };
  const archive = async () => { if (!window.confirm("Archive this?")) return; await osFetch(`${cfg.base}/${id}/archive`, { method: "POST" }); toast.success("Archived"); navigate(`/os/${moduleKey}`); };
  const onAI = (ai) => setData({ ...data, entity: { ...e, ai } });

  return (
    <div className="max-w-6xl" data-testid={`os-${moduleKey}-detail`}>
      <Link to={`/os/${moduleKey}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4" data-testid={`os-${moduleKey}-back`}><ArrowLeft size={14} /> {cfg.label}</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3"><Icon className="text-[#00F0FF]" />
            {edit ? <input value={ed[cfg.titleKey]} onChange={(ev) => setEdit({ ...ed, [cfg.titleKey]: ev.target.value })} className="os-input text-2xl font-bold" data-testid="os-edit-title" />
              : <h1 className="font-display font-extrabold text-3xl truncate" data-testid={`os-${moduleKey}-name`}>{e[cfg.titleKey]}</h1>}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {e.status && <span className={`text-xs font-mono uppercase tracking-widest ${STATUS_COLOR[e.status] || "text-slate-400"}`}>{e.status}</span>}
            {e.horizon && <span className="text-xs font-mono text-slate-500">· {e.horizon}</span>}
            <span className="text-xs font-mono text-slate-600">· v{e.current_version}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {can("update") && !edit && <button onClick={startEdit} className="border border-white/15 px-4 py-2.5 rounded-xl text-sm hover:border-white/30" data-testid={`os-${moduleKey}-edit`}>Edit</button>}
          {edit && <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#0055FF] px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid={`os-${moduleKey}-save`}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>}
          {edit && <button onClick={() => setEdit(null)} className="border border-white/15 px-4 py-2.5 rounded-xl text-sm">Cancel</button>}
          {can("delete") && !edit && <button onClick={archive} className="border border-rose-500/30 text-rose-300 px-3 py-2.5 rounded-xl text-sm hover:bg-rose-500/10" data-testid={`os-${moduleKey}-archive`}><Archive size={14} /></button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <div className="lg:col-span-2 space-y-4">
          {/* Living project vitals */}
          {cfg.showHealth && live && (
            <Card icon={Gauge} title="Vitals" testid="os-project-vitals">
              <div className="grid grid-cols-3 gap-4">
                <Vital label="Live health" value={live.live_health} suffix="/100" tone={live.live_health >= 70 ? "good" : live.live_health >= 40 ? "warn" : "bad"} />
                <Vital label="Confidence" value={live.confidence_score} suffix="/100" tone={live.confidence_score >= 70 ? "good" : live.confidence_score >= 40 ? "warn" : "bad"} />
                <Vital label="Open attention" value={live.open_attention} tone={live.open_attention === 0 ? "good" : live.open_attention <= 2 ? "warn" : "bad"} />
              </div>
              {Object.keys(live.linked_counts || {}).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.entries(live.linked_counts).map(([t, n]) => <span key={t} className="text-[10px] font-mono uppercase tracking-widest text-slate-400 border border-white/10 rounded-full px-2.5 py-1">{t.replace("_", " ")} · {n}</span>)}
                </div>
              )}
            </Card>
          )}

          {/* Bible sections OR generic fields */}
          {cfg.special === "bible" ? (
            <div className="space-y-3">
              {(edit ? ed.sections : e.sections || []).map((s, i) => (
                <Card key={s.key} title={s.title} testid={`os-bible-section-${s.key}`}>
                  {edit ? <textarea rows={3} className="os-input" value={ed.sections[i].content} onChange={(ev) => { const ns = [...ed.sections]; ns[i] = { ...ns[i], content: ev.target.value }; setEdit({ ...ed, sections: ns }); }} />
                    : <p className="text-slate-300 whitespace-pre-wrap text-sm">{s.content || "—"}</p>}
                </Card>
              ))}
            </div>
          ) : (
            cfg.fields.filter((f) => f.type !== "select" || edit).map((f) => (
              <Card key={f.key} title={f.label} testid={`os-card-${f.key}`}>
                {edit ? <Field f={f} value={ed[f.key]} onChange={(k, v) => setEdit({ ...ed, [k]: v })} />
                  : <p className="text-slate-300 whitespace-pre-wrap">{String(e[f.key] ?? "—") || "—"}</p>}
              </Card>
            ))
          )}

          {/* Read-only arrays (business brief) */}
          {!edit && (cfg.arrays || []).map((a) => (
            <Card key={a.key} title={a.label} testid={`os-array-${a.key}`}>
              {(e[a.key] || []).length === 0 ? <p className="text-slate-600 text-sm">—</p> : <ul className="space-y-1">{(e[a.key] || []).map((v, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-slate-600">•</span>{v}</li>)}</ul>}
            </Card>
          ))}

          <AIPanel entityType={cfg.entityType} entityId={id} base={cfg.base} ai={e.ai} onUpdate={onAI} />
        </div>

        <div className="space-y-4">
          <EntityAttentionPanel entityType={cfg.entityType} entityId={id} />
          <ConnectedPanel backlinks={data.backlinks} links={data.links} />
          <ChangedPanel activity={data.activity} history={data.history} base={cfg.base} entityId={id} onRestored={load} />
        </div>
      </div>
    </div>
  );
}

const Vital = ({ label, value, suffix = "", tone }) => (
  <div>
    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`font-display font-extrabold text-2xl ${tone === "good" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-rose-300"}`}>{value ?? "—"}<span className="text-sm text-slate-600">{suffix}</span></p>
  </div>
);
